import { create } from "zustand"
import { type MeMetric } from "@/features/auth/hooks/useCurrentUser"
import { apiClient } from "@/services/api.client"
import { AllowanceHint } from "@/features/billing/components/AllowanceHint"
import { useCheckout } from "@/features/billing/hooks/useCheckout"
import { FALLBACK_ADDON_MINOR, rupees } from "@/features/billing/plan"
import { useEffect } from "react"
import { inlineEdits, parseQuotaDenial, type QuotaDenial } from "@/lib/quotaGate"
import { track } from "@/lib/analytics"

/**
 * The 11th edit on one draft (paywall trigger 14). It is said inline, under the draft or the row being
 * changed, never as a modal: what is on the page stays, the edit that was refused is simply not made, and
 * the one way forward is "5 more edits for ₹299" for this draft. The sample case is never metered.
 */

const SKU = "extra_edits"

interface EditLimitState {
  /** The refusal per draft session; cleared once the add-on is bought. */
  byThread: Record<string, QuotaDenial | undefined>
  set: (threadId: string, d: QuotaDenial | undefined) => void
  /** This draft's own edit allowance (GET /billing/usage), which /me cannot know. */
  usage: Record<string, MeMetric | undefined>
  setUsage: (threadId: string, m: MeMetric | undefined) => void
}

export const useEditLimit = create<EditLimitState>((set) => ({
  byThread: {},
  set: (threadId, d) => set((s) => ({ byThread: { ...s.byThread, [threadId]: d } })),
  usage: {},
  setUsage: (threadId, m) => set((s) => ({ usage: { ...s.usage, [threadId]: m } })),
}))

/** Ask the gateway how many edits this draft has used and may use. Quiet on failure: no hint is better than a wrong one. */
export async function loadEditAllowance(threadId: string): Promise<void> {
  try {
    const m = await apiClient.get<MeMetric>(
      `/api/v1/billing/usage?metric=order.draft_edit&scope_key=${encodeURIComponent(threadId)}`,
    )
    if (m && typeof m === "object" && "used" in m) useEditLimit.getState().setUsage(threadId, m)
  } catch {
    /* the hint simply does not show */
  }
}

/** True when `err` is the gateway's 402 for this draft's edits (and remembers it so the line shows). */
export function noteEditRefusal(threadId: string, err: unknown): boolean {
  const e = err as { status?: unknown; quota?: unknown } | null
  if (!e || e.status !== 402) return false
  const d = parseQuotaDenial(e.quota)
  if (!d || d.metric !== "order.draft_edit") return false
  useEditLimit.getState().set(threadId, d)
  return true
}

const refreshTimers: Record<string, ReturnType<typeof setTimeout> | undefined> = {}
/** After an edit lands: ask for this draft's allowance again, at most every few seconds, so "2 left" shows when it is true. */
export function refreshEditAllowance(threadId: string): void {
  if (refreshTimers[threadId]) return
  refreshTimers[threadId] = setTimeout(() => {
    refreshTimers[threadId] = undefined
    void loadEditAllowance(threadId)
  }, 1500)
}

/** Under a draft or a change: what is left from 2, and the refused edit with the add-on, inline. */
export function EditLimitLine({ threadId, className = "" }: { threadId: string; className?: string }) {
  const denial = useEditLimit((s) => s.byThread[threadId])
  const usage = useEditLimit((s) => s.usage[threadId])
  const checkout = useCheckout()
  useEffect(() => { void loadEditAllowance(threadId) }, [threadId])
  // While this line is on the page the 402 for edits is said here, not as a modal as well.
  useEffect(() => {
    inlineEdits.mounted += 1
    return () => { inlineEdits.mounted -= 1 }
  }, [])
  const price = denial?.addon?.priceMinor ?? FALLBACK_ADDON_MINOR[SKU]
  const qty = denial?.addon?.quantity ?? 5
  const buy = async () => {
    track("addon_clicked", { sku: SKU, trigger: 14, inline: true })
    if ((await checkout.buy(SKU, threadId)) === "paid") useEditLimit.getState().set(threadId, undefined)
  }
  return (
    <div className={`nel${className ? ` ${className}` : ""}`}>
      <AllowanceHint metric="order.draft_edit" data={usage} />
      {denial ? (
        <p className="nel-line" role="status">
          {denial.message ?? "This draft's edits are used."}{" "}
          <button type="button" className="pro-link" disabled={!!checkout.busy} onClick={() => void buy()}>
            {checkout.busy === SKU ? "Opening checkout…" : `${qty} more edits for ${rupees(price)}`}
          </button>
        </p>
      ) : null}
      {checkout.error ? <p className="nel-line" role="alert">{checkout.error}</p> : null}
    </div>
  )
}
